import React, { useState, useEffect, useRef } from "react";
import { Send, Users, Smile, Dot } from "lucide-react";
import axios from "axios";

function WatchPartyChat({
  partyId,
  socketManager,
  participants,
  isFullscreen = false,
}) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const emojiPopoverRef = useRef(null);

  const emojis = {
    smileys: [
      "😀","😃","😄","😁","😅","😂","🤣","😊","😇","🙂","🙃","😉","😌","😍","🥰","😘","😗","😙","😚","😋","😛","😝","😜","🤪","🤨","🧐","🤓","😎","🤩","🥳",
    ],
    gestures: [
      "👍","👎","👌","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","👇","☝️","✋","🤚","🖐️","🖖","👋","🤝","🙏","💪","🦾","🦿",
    ],
    hearts: [
      "❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💞","💓","💗","💖","💘","💝",
    ],
    fun: ["🎉","🎊","🎈","🎁","🏆","🥇","🥈","🥉","⭐","🌟","✨","💫","🔥","💯","✅","❌"],
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        const response = await axios.get(`/api/watch-parties/${partyId}/messages`);
        setMessages(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error loading chat history:", error);
      }
    };
    if (partyId) loadChatHistory();
  }, [partyId]);

  useEffect(() => {
    if (!socketManager) return;

    const handleChatMessage = (data) => setMessages((prev) => [...prev, data]);
    socketManager.on("chat-message", handleChatMessage);
    return () => socketManager.off("chat-message", handleChatMessage);
  }, [socketManager]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!showEmojiPicker) return;

    const onMouseDown = (e) => {
      const pop = emojiPopoverRef.current;
      const btn = e.target.closest?.("[data-emoji-trigger]");
      if (btn) return;
      if (pop && !pop.contains(e.target)) setShowEmojiPicker(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowEmojiPicker(false);
    };

    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showEmojiPicker]);

  const handleEmojiClick = (emoji) => {
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart ?? inputMessage.length;
    const end = input.selectionEnd ?? inputMessage.length;

    const next = inputMessage.slice(0, start) + emoji + inputMessage.slice(end);
    setInputMessage(next);

    setTimeout(() => {
      input.focus();
      const pos = start + emoji.length;
      input.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = inputMessage.trim();
    if (!trimmed || isSending) return;

    if (!socketManager || !socketManager.isConnected) {
      alert("Not connected to chat. Please refresh the page.");
      return;
    }

    setIsSending(true);
    try {
      socketManager.sendMessage(trimmed);
      setInputMessage("");
      setShowEmojiPicker(false);
    } catch (error) {
      alert("Failed to send message: " + (error?.message || "Unknown error"));
    } finally {
      setIsSending(false);
    }
  };

  const formatMessageTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  /**
   * Height behavior:
   * - stop growth forever (cap height + internal scroll)
   * - make it SHORTER: about ~75% of viewport height (per your screenshot)
   *
   * You can tweak `75vh` -> `70vh` / `72vh` if you want more breathing room.
   */
  const panelHeightClass = isFullscreen
    ? "h-full max-h-full"
    : "h-[60vh] max-h-[60vh]"; // shorter so it won’t block other UI

  // Blue-ish glass base (less gray)
  const panelBase = [
    "rounded-2xl border border-white/10",
    "bg-[#07162e]/70",
    "backdrop-blur-2xl",
    "ring-1 ring-white/5",
    "overflow-hidden",
    "shadow-[0_22px_80px_rgba(0,0,0,0.55)]",
  ].join(" ");

  const topBar = [
    "border-b border-white/10",
    "bg-[#061226]/75",
    "backdrop-blur-2xl",
  ].join(" ");

  const bottomBar = [
    "border-t border-white/10",
    "bg-[#061226]/75",
    "backdrop-blur-2xl",
  ].join(" ");

  const softInset =
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.06),inset_0_-1px_0_rgba(0,0,0,0.35)]";

  return (
    <div
      className={[
        "flex flex-col min-h-0 overflow-hidden",
        panelHeightClass,
        panelBase,
        softInset,
        isFullscreen ? "shadow-[0_26px_92px_rgba(0,0,0,0.65)]" : "",
      ].join(" ")}
    >
      {/* Header */}
      <div className={["flex items-center justify-between px-4 py-3", topBar].join(" ")}>
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-[13px] font-semibold tracking-wide text-white/90">
            Chat
          </h3>

          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-[11px] font-medium text-emerald-200/90">
            <Dot size={16} className="text-emerald-300" />
            Live
          </span>
        </div>

        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          <Users size={14} className="text-white/65" />
          <span className="text-[12px] font-medium text-white/70">
            {participants?.length || 0}
          </span>
        </div>
      </div>

      {/* Messages (the only scroll area) */}
      <div
        className={[
          "flex-1 min-h-0 overflow-y-auto",
          "px-4 py-4",
          "bg-[#061226]/30",
        ].join(" ")}
      >
        {messages.length === 0 ? (
          <div className="grid h-full place-items-center">
            <div className="text-center">
              <p className="text-sm text-white/70">No messages yet.</p>
              <p className="mt-1 text-xs text-white/45">Start the conversation.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m, idx) => {
              const avatar =
                m.avatar || m.profile_picture || "https://i.pravatar.cc/40";

              return (
                <div key={m.id || idx} className="flex items-start gap-3">
                  <img
                    src={avatar}
                    alt={m.username}
                    className="h-9 w-9 rounded-full border border-white/10 object-cover shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                    loading="lazy"
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-[12.5px] font-semibold text-white/90">
                        {m.username}
                      </span>
                      <span className="text-[11px] text-white/40">
                        {formatMessageTime(m.created_at)}
                      </span>
                    </div>

                    <div className="mt-1 inline-block max-w-full rounded-2xl border border-white/10 bg-[#0a1d38]/35 px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                      <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-white/80">
                        {m.message}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className={["relative px-4 py-3", bottomBar].join(" ")}>
        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div
            ref={emojiPopoverRef}
            className={[
              "absolute bottom-full right-4 mb-3 w-[22rem] overflow-hidden rounded-2xl",
              "border border-white/10 bg-[#061a33]/80 backdrop-blur-2xl",
              "shadow-[0_28px_110px_rgba(0,0,0,0.75)]",
              "ring-1 ring-white/5",
            ].join(" ")}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 bg-black/25">
              <span className="text-[12px] font-semibold text-white/75">Emojis</span>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(false)}
                className="rounded-lg border border-white/10 bg-white/[0.05] px-2 py-1 text-[11px] text-white/65 hover:bg-white/[0.08]"
              >
                Esc
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto p-3 space-y-3">
              {[
                ["Smileys", emojis.smileys],
                ["Gestures", emojis.gestures],
                ["Hearts", emojis.hearts],
                ["Fun", emojis.fun],
              ].map(([label, list]) => (
                <div key={label}>
                  <p className="mb-2 text-[11px] font-medium tracking-wide text-white/45">
                    {label}
                  </p>
                  <div className="grid grid-cols-9 gap-1.5">
                    {list.map((emoji, i) => (
                      <button
                        key={`${label}-${i}`}
                        type="button"
                        onClick={() => handleEmojiClick(emoji)}
                        className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/[0.05] text-xl hover:bg-white/[0.08] active:scale-[0.98]"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <div
            className={[
              "flex flex-1 items-center gap-2 rounded-2xl px-3 py-2",
              "border border-white/10 bg-white/[0.05]",
              "shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]",
              "focus-within:ring-2 focus-within:ring-emerald-400/30",
            ].join(" ")}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Send a message…"
              disabled={isSending}
              maxLength={500}
              className="w-full bg-transparent text-[13px] text-white/90 placeholder:text-white/35 focus:outline-none disabled:opacity-50"
            />

            <button
              type="button"
              data-emoji-trigger
              onClick={() => setShowEmojiPicker((v) => !v)}
              className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/[0.05] text-white/75 hover:bg-white/[0.08] active:scale-[0.98]"
              title="Add emoji"
            >
              <Smile size={18} />
            </button>
          </div>

          <button
            type="submit"
            disabled={!inputMessage.trim() || isSending}
            className={[
              "grid h-11 w-11 place-items-center rounded-2xl",
              "border border-emerald-400/20",
              "bg-emerald-400/10 text-emerald-100",
              "shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_16px_40px_rgba(0,0,0,0.45)]",
              "hover:bg-emerald-400/15 active:scale-[0.99] transition",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            ].join(" ")}
            title="Send"
          >
            <Send size={18} />
          </button>
        </div>

        <div className="mt-2 flex items-center justify-between text-[10px]">
          <span className={socketManager?.isConnected ? "text-emerald-300/80" : "text-white/40"}>
            {socketManager?.isConnected ? "Connected" : "Disconnected"}
          </span>
          <span className="text-white/40">{inputMessage.length}/500</span>
        </div>
      </form>
    </div>
  );
}

export default WatchPartyChat;
